import { Plus, RefreshCw, Upload } from 'lucide-react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { LoginMascotBanner } from '../components/LoginMascotBanner'
import { countPrimaryLoginProgress, StateLoginsTable } from '../components/StateLoginsTable'
import { LoginStatesIntro } from '../licenseSteps/LoginStatesIntro'
import { MISC_PRIMARY, MISC_PRIMARY_HOVER } from '../../theme/onboardingTheme'

const toolbarBtnSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  borderRadius: '4px',
  backgroundColor: MISC_PRIMARY,
  px: 1.5,
  py: 1,
  fontSize: 12,
  fontWeight: 600,
  color: '#ffffff',
  boxShadow: 1,
  textTransform: 'none' as const,
  '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
}

export function StepLogins() {
  const { state, dispatch } = useOnboarding()
  const { needed, done } = countPrimaryLoginProgress(state.stateLogins, state.licenses)

  const primaryToolbar = (
    <>
      <Button
        type="button"
        title="Refresh"
        onClick={() => dispatch({ type: 'TOAST', message: 'Table refreshed (demo).' })}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 0,
          border: '1px solid #cbd5e1',
          backgroundColor: '#ffffff',
          p: 1,
          color: '#475569',
          '&:hover': { backgroundColor: '#f8fafc' },
        }}
      >
        <RefreshCw size={16} />
      </Button>
      <Button
        type="button"
        onClick={() => dispatch({ type: 'TOAST', message: 'Import would open (demo).' })}
        sx={toolbarBtnSx}
      >
        <Upload size={16} />
        Import
      </Button>
      <Button type="button" onClick={() => dispatch({ type: 'ADD_STATE_LOGIN' })} sx={toolbarBtnSx}>
        <Plus size={16} />
        Add
      </Button>
    </>
  )

  return (
    <Stack spacing={2.5}>
      <LoginStatesIntro />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0, maxWidth: '42rem', flex: 1 }}>
          <Typography
            component="h2"
            sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em', color: '#0f172a' }}
          >
            State Logins
          </Typography>
          <Typography
            component="p"
            sx={{
              mt: 1,
              maxWidth: { xs: '56rem', lg: 'none' },
              fontSize: 14,
              lineHeight: 1.625,
              color: '#475569',
            }}
          >
            Please enter your credentials for each state filing system below to ensure timely filings
            on your behalf. If you don&apos;t have a login yet, check <strong>No Login</strong> for
            that row — you&apos;ll complete setup on the <strong>New Login Wizard</strong> step next.
          </Typography>
        </Box>
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            alignSelf: 'flex-start',
            borderRadius: 999,
            border: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            px: 1.5,
            py: 0.75,
            fontSize: 12,
            fontWeight: 600,
            color: '#334155',
          }}
        >
          {done} of {needed.length} complete
        </Box>
      </Box>

      {state.miscHandledStates.length === 0 ? (
        <Box
          sx={{
            borderRadius: '12px',
            border: '1px solid #fde68a',
            backgroundColor: '#fffbeb',
            px: 2,
            py: 1.5,
            fontSize: 14,
            color: '#1e293b',
          }}
        >
          Select at least one state on General Questions so we can list the filing systems you need.
        </Box>
      ) : (
        <>
          <LoginMascotBanner toolbar={primaryToolbar} />
          <StateLoginsTable rows={state.stateLogins} mode="primary" />
        </>
      )}
    </Stack>
  )
}
